import React, { useState, useEffect, useRef, useCallback } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Modal, Tabs, Dropdown, Button, message } from 'antd'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { safeboxClient } from 'myNet'
import { store as reduxStore } from 'index'
import { getTempEnvParam, removeTempEnvParam } from 'env'

import {
  getUMKFromLocal,
  setUMKToLocal,
  WRKP_ESCROW_ENABLED,
  RECOVERY_OPTION,
  redirectToMasterPwReenter,
} from 'pUtils'
import { confirmBase } from 'utils/ui'
import { useIsMobile } from 'utils'
import { isGlobal, isKR } from 'conf'
import { isPasskeyEnabled } from 'services/passkey'
import UserService from 'api/Users'
import ProfilePhoto from 'components/ProfilePhoto'
import { notifyExtensionClipboardSetting } from 'utils/extensionBridge'
import backIcon from '@/assets/images/arrow-left.svg'
import closeIcon from '@/assets/images/button-remove.svg'
import cameraIcon from '@/assets/images/icon-profile-camera.svg'

import styles from './style.module.scss'
import SettingsService from './api/Settings.ts'
import {
  AccountIcon,
  SettingIcon,
  SecurityIcon,
  CompanyIcon,
  AboutIcon,
  ZeroDarkWebIcon,
  PasskeyIcon,
} from './icon'
import ZeroDarkWebRender, { handleSaveZeroDarkWeb } from './components/ZeroDarkWeb'
import AboutRender from './components/About'
import WorkspaceRender from './components/Workspace'
import GeneralRender from './components/General'
import SecurityRender from './components/Security'
import AccountRender from './components/Account'
import PasskeyManagement from './components/PasskeyManagement'

const COLORS = {
  BLACK: '#1F2937',
  BLUE: '#3362FF',
}

export const autoLockOptions = (t) => {
  return [
    { label: t('appConfig.settings.1minute'), key: '1', milliseconds: 1 * 60 * 1000 },
    { label: t('appConfig.settings.5minutes'), key: '5', milliseconds: 5 * 60 * 1000 },
    { label: t('appConfig.settings.10minutes'), key: '10', milliseconds: 10 * 60 * 1000 },
    { label: t('appConfig.settings.15minutes'), key: '15', milliseconds: 15 * 60 * 1000 },
    { label: t('appConfig.settings.30minutes'), key: '30', milliseconds: 30 * 60 * 1000 },
    { label: t('appConfig.settings.1hour'), key: '60', milliseconds: 60 * 60 * 1000 },
    { label: t('appConfig.settings.off'), key: '0', milliseconds: 0 },
  ]
}

// 클립보드 자동삭제 유지시간 옵션. key='0' = Never(off), '1'~'5' = 분.
// auto_lock 패턴 재사용. minutes(분) 단위 — clipboardClearMs 는 ms.
export const clipboardClearOptions = (t) => {
  return [
    { label: t('appConfig.settings.clipboardNever'), key: '0', minutes: 0 },
    { label: t('appConfig.settings.1minute'), key: '1', minutes: 1 },
    { label: t('appConfig.settings.2minutes'), key: '2', minutes: 2 },
    { label: t('appConfig.settings.3minutes'), key: '3', minutes: 3 },
    { label: t('appConfig.settings.4minutes'), key: '4', minutes: 4 },
    { label: t('appConfig.settings.5minutes'), key: '5', minutes: 5 },
  ]
}

const mapStateToProps = ({ user, settings }) => ({
  role: user.role,
  workspaceName: user.workspace?.workspace_name,
  workspaceId: user.workspace?.id,
  locale: settings.locale,
  umkDuration: settings.umkDuration,
  clipboardClearMs: settings.clipboardClearMs,
  recoverKeyEnabled: settings.recoverKeyEnabled,
  recoveryOptionPersisted: user.recovery_option,
  // /me 응답의 워크스페이스 복구키 초기화 여부 — Security 탭 Server 옵션 게이팅
  wrkpInitialized: user.wrkp_initialized,
  user,
  userName: user.name,
  userEmail: user.email,
  authType: user.authType,
  provider: user.provider,
})

const SettingsPopup = (props) => {
  const {
    umkDuration,
    clipboardClearMs,
    locale,
    recoverKeyEnabled,
    open,
    onClose,
    user,
    userName,
    workspaceName: workspaceNameProps,
    workspaceId: workspaceIdProps,
    setSettingsOpen,
    userEmail,
    provider,
    recoveryOptionPersisted,
    wrkpInitialized,
  } = props

  // SAML 사용자: IdP가 source-of-truth — 이름 변경 폼 미노출 (백엔드도 511로 차단).
  // provider는 JWT claim으로 매 토큰 발급 시 갱신 — SAML→Basic 전환 시 자동 해제됨.
  // (auth_type은 가입 상태이므로 SAML 여부 판단 불가 — provider 사용)
  const isSamlUser = provider === 'saml'

  const { t } = useTranslation()

  const getInitialAutoLockKey = () => {
    if (umkDuration) {
      const option = autoLockOptions(t).find((opt) => opt.milliseconds === Number(umkDuration))
      return option ? option.key : 3
    }
    return null
  }

  // 클립보드 유지시간 현재값(ms) → 드롭다운 key. 미설정/0 → '0'(Never).
  const getInitialClipboardClearKey = () => {
    const minutes = clipboardClearMs ? Number(clipboardClearMs) / (60 * 1000) : 0
    const option = clipboardClearOptions(t).find((opt) => opt.minutes === minutes)
    return option ? option.key : '0'
  }

  const getInitialZeroDarkWeb = () => {
    const zdwSettings = reduxStore.getState().settings.zeroDarkWebSettings

    const value = (label) => zdwSettings?.[label]
    const toBool = (v) => {
      if (typeof v === 'boolean') return v
      if (typeof v === 'string') return v.toLowerCase() === 'true'
      return false
    }

    return {
      switch: toBool(value('zdw_active')) || false,
      interval:
        value('zdw_duration') != null && value('zdw_duration') !== 'null'
          ? String(value('zdw_duration'))
          : '7',
    }
  }

  const defaultZeroDarkWebSettings = getInitialZeroDarkWeb()

  const [activeTab, setActiveTab] = useState(1)
  const [autoLock, setAutoLock] = useState(null)
  const [clipboardClear, setClipboardClear] = useState(() => getInitialClipboardClearKey())
  const [messageApi, contextHolder] = message.useMessage()
  const isOpenZDWSettingsRef = useRef(false)

  const [isEditingTeamName, setIsEditingTeamName] = useState(false)
  const [isEditingEmail, setIsEditingEmail] = useState(false)

  const [depth, setDepth] = useState(0)

  const [recoveryChecked, setRecoveryChecked] = useState(false)

  // PR2: 복구정책 드롭다운 현재값(H1 persisted 동기화) + ESCROW 전환/D-GATE용 wrkp 공개키 목록.
  const [recoveryOption, setRecoveryOption] = useState(RECOVERY_OPTION.NONE)
  const [wrkpPublicKeys, setWrkpPublicKeys] = useState([])

  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState(user.avatar)
  const [zeroDarkWebSettings, setZeroDarkWebSettings] = useState(defaultZeroDarkWebSettings)

  const isMobile = useIsMobile()
  const dispatch = useDispatch()
  const ref = useRef(null)
  const fileInputRef = useRef(null)

  const changeColor = (tabNum) => {
    if (activeTab === tabNum) {
      return COLORS.BLUE
    }
    return COLORS.BLACK
  }

  // 자동 잠금: 드롭다운 선택 즉시 적용(footer 저장 버튼 제거) — 같은 화면의 복구 위치 드롭다운과
  // 동일한 즉시-반영 패턴. 모달은 닫지 않고(상태만 갱신), 변경분이 서버에 바로 반영된다.
  const handleAutoLockChange = async (targetKey) => {
    if (targetKey === autoLock) return
    setAutoLock(targetKey)

    const selectedOption = autoLockOptions(t).find((option) => option.key === targetKey)
    if (!selectedOption || Number(selectedOption.key) === getInitialAutoLockKey()) return

    const UMK = await getUMKFromLocal()
    setUMKToLocal(UMK, {
      duration: selectedOption.milliseconds === 0 ? 0 : selectedOption.milliseconds,
    })

    try {
      const res = await SettingsService.put({
        autoLock: selectedOption.key != null ? selectedOption.key : '0',
      })

      if (res.code === 620) {
        await SettingsService.create({
          autoLock: selectedOption.key != null ? selectedOption.key : '0',
          local: locale.substring(0, 2),
          recoveryKeyToEmail:
            recoverKeyEnabled === 'email' && recoverKeyEnabled != null ? 'email' : 'local',
        })
      }

      messageApi.success(t('appConfig.result.successAutoLock'))
    } catch (error) {
      console.log('error - ', error)
      messageApi.error(t('appConfig.settings.failedAutoLock'))
    }
  }

  // 클립보드 유지시간 변경(드롭다운 즉시 적용). auto_lock 과 동일 즉시-반영 패턴.
  // putClipboardClear 가 별도 PUT(422 격리) + redux/localStorage 반영을 담당하고 실패는 조용히 폴백.
  // 익스텐션 통지는 로컬 반영(applied)된 경우에만 보낸다 — 실패 시 익스텐션 캐시만 새 값이 되어
  // redux/web 스케줄러와 어긋나는 것을 방지.
  const handleClipboardClearChange = async (targetKey) => {
    if (targetKey === clipboardClear) return
    setClipboardClear(targetKey)
    const applied = await SettingsService.putClipboardClear(targetKey)
    if (applied) notifyExtensionClipboardSetting(targetKey)
  }

  // PR2: 복구정책 전환(드롭다운 선택 → PATCH /me/recovery-option). recovery_priv 서버 미전송(ZK).
  // ⚠️ 낙관적 업데이트 지양(gemini PR#1251): PATCH 성공 후에만 setRecoveryOption + (LOCAL) PEM 다운로드 →
  //   깜빡임/상태불일치/무효 PEM 다운로드(=계정 잠금) 방지. 실패 시 상태 미변경이라 롤백 불요.
  const applyRecoveryOption = async (targetOption) => {
    setLoading(true)
    try {
      let wrkpList = []
      if (targetOption === RECOVERY_OPTION.ESCROW) {
        // F2: escrowed=true인데 wrkp_pub 없는 ws는 새 keypair 회전 시 stale escrow(전역 enc_umk 불일치) → 차단.
        wrkpList = wrkpPublicKeys
        const hasStaleRisk = wrkpList.some((item) => item.escrowed && !item.wrkp_public_key)
        if (hasStaleRisk) {
          messageApi.error(t('appConfig.settings.recoveryEscrowStaleBlocked'))
          return
        }
      }
      const rsaObj = getTempEnvParam('rsaObj')
      const { payload, localPem } = await rsaObj.buildRecoveryOptionPayload(targetOption, wrkpList)
      await UserService.updateRecoveryOption(payload)
      // 성공 후에만: UI 상태 갱신 + (LOCAL) recovery_priv PEM 다운로드.
      setRecoveryOption(targetOption)
      if (localPem) {
        rsaObj.savePrivateKeyLocally(localPem)
      }
      dispatch({ type: 'user/SET_STATE', payload: { recovery_option: targetOption } })
      messageApi.success(t('appConfig.result.successRecoveryOption'))
    } catch (error) {
      // 에러는 HTTP200+body.code(828/829/807 등) — 일반 메시지. 상태 미변경(낙관적 업데이트 없음).
      // 844(recovery_pending 중 전환 차단)만 전용 안내 — 탈출 방법(비밀번호 변경 완료)을 알 수 있게 (#1320).
      console.log('recovery_option change error - ', error?.code)
      if (error?.code === 844) {
        messageApi.error(t('workspace.error.844'))
      } else if (error?.code === 845) {
        // 845(세션 UMK 세대 불일치): 세션 캐시가 서버 최신 UMK보다 뒤처짐 → 마스터 비번 재입력 유도.
        // [잠금화면으로 이동]=redirectToMasterPwReenter, [취소]=아무 동작 없이 닫기(현재 화면 유지).
        // toast 대신 확인 팝업으로 사유를 알리고 이동을 사용자 선택에 맡긴다.
        confirmBase({
          icon: 'red',
          width: 480,
          title: t('common.title.warning'),
          content: t('workspace.error.845'),
          okText: t('common.button.goToLockScreen'),
          cancelText: t('common.button.cancel'),
          onOk: redirectToMasterPwReenter,
        })
      } else {
        messageApi.error(t('appConfig.settings.failedRecoveryOption'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleRecoveryOptionChange = (targetOption) => {
    if (targetOption === recoveryOption) return

    // NONE: 전역 enc_umk + escrow 제거(복구 불가) — 파괴적이라 확인. (F4: 멀티 ws ESCROW 잔존 시 고아화 주의)
    // confirmBase: 프로젝트 공통 확인 팝업(om-popup-1 레이아웃) 사용
    if (targetOption === RECOVERY_OPTION.NONE) {
      confirmBase({
        icon: 'red',
        width: 480,
        title: t('appConfig.settings.recoveryNoneConfirmTitle'),
        content: t('appConfig.settings.recoveryNoneConfirmDesc'),
        okText: t('common.button.confirm'),
        cancelText: t('common.button.cancel'),
        onOk: () => applyRecoveryOption(targetOption),
      })
      return
    }

    applyRecoveryOption(targetOption)
  }

  // 다크웹 모니터링: 스위치/검사 주기 변경 즉시 적용(footer 저장 버튼 제거). 로컬 상태 갱신 +
  // 서버 저장(만료/체크 시각 계산·redux 동기화·데이터 패치 이벤트)을 한 번에 수행.
  const handleZeroDarkWebChange = (newSettings) => {
    setZeroDarkWebSettings(newSettings)
    handleSaveZeroDarkWeb(newSettings, messageApi, t)
  }

  const handleCancel = () => {
    ref?.current?.resetFields()
    setAutoLock(getInitialAutoLockKey())
    setIsEditingTeamName(false)
    setIsEditingEmail(false)
    setDepth(0)
    setSettingsOpen(false)
    onClose()
    const latestSettings = getInitialZeroDarkWeb()
    setZeroDarkWebSettings(latestSettings)

    if (isMobile) {
      setActiveTab(0)
    }

    if (getTempEnvParam('isOpenZDWSettings') === true) {
      removeTempEnvParam('isOpenZDWSettings')
    }
  }

  const handleUploadAvatar = async (file) => {
    const isJpgOrPng = file?.type === 'image/jpeg' || file?.type === 'image/png'
    if (!isJpgOrPng) {
      messageApi.error(t('appConfig.settings.avatarFormat'))
      return false
    }
    const isLt2M = (file?.size ?? 0) / 1024 / 1024 < 2
    if (!isLt2M) {
      messageApi.error(t('appConfig.settings.avatarSize'))
      return false
    }

    setUploading(true)

    try {
      const reader = new FileReader()
      const fileData = await new Promise((resolve) => {
        reader.onload = (e) => resolve(e.target.result)
        reader.readAsArrayBuffer(file)
      })

      const hashBuffer = await crypto.subtle.digest('SHA-256', fileData)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const etag = hashArray
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
        .substring(0, 32)

      const formData = new FormData()

      formData.append('file', file, file.name)
      formData.append(
        'data',
        JSON.stringify({
          filename: file.name,
          size: file.size,
          content_type: file.type,
          etag,
        }),
      )

      const { workspaceId } = props
      const hasAvatar = user.avatar && user.avatar !== ''
      const method = hasAvatar ? 'xhrPut' : 'xhrPost'

      const res = await safeboxClient[method](
        `/v1/user_avatar/me/avatar?workspace-id=${workspaceId}`,
        formData,
        null,
        null,
        {
          headers: {},
        },
      )

      if (res?.status === 200) {
        messageApi.success(t('appConfig.result.successChangeAvatar'))

        const avatarUrl =
          res.data?.avatar?.url ||
          res.data?.url ||
          res.data?.avatar_url ||
          res.data?.data?.url ||
          res.data?.data?.avatar_url

        console.log('Avatar upload response:', res)
        console.log('Extracted avatar URL:', avatarUrl)

        if (avatarUrl) {
          setCurrentAvatarUrl(avatarUrl)

          dispatch({
            type: 'user/SET_USER_AVATAR',
            payload: { avatar: avatarUrl },
          })
        } else {
          console.error('Avatar URL not found in response:', res)
        }
      } else {
        messageApi.error(t('appConfig.result.failedChangeAvatar'))
      }
    } catch (error) {
      console.error('Upload error:', error)
      messageApi.error(t('appConfig.result.failedChangeAvatar'))
    } finally {
      setUploading(false)
    }

    return false
  }

  const handleRemoveAvatar = async () => {
    try {
      setUploading(true)

      const { workspaceId } = props
      const res = await safeboxClient.delete(
        `/v1/user_avatar/me/avatar?workspace-id=${workspaceId}`,
      )

      if (res?.status === 200 || res?.data?.code === 200) {
        messageApi.success(t('appConfig.result.successRemoveAvatar'))

        setCurrentAvatarUrl('')

        dispatch({
          type: 'user/SET_USER_AVATAR',
          payload: { avatar: '' },
        })
      } else {
        messageApi.error(t('appConfig.result.failedRemoveAvatar'))
      }
    } catch (error) {
      console.error('Remove avatar error:', error)
      messageApi.error(t('appConfig.result.failedRemoveAvatar'))
    } finally {
      setUploading(false)
    }
  }

  const triggerFileUpload = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event) => {
    const file = event.target.files?.[0]
    if (file) {
      handleUploadAvatar(file)
      event.target.value = ''
    }
  }

  const renderProfilePhoto = () => {
    return (
      <React.Fragment>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Dropdown
          menu={{
            items: [
              {
                key: 'change',
                label: currentAvatarUrl
                  ? t('appConfig.settings.changePhoto')
                  : t('appConfig.settings.addPhoto'),
                onClick: triggerFileUpload,
              },
              currentAvatarUrl && {
                key: 'remove',
                label: t('appConfig.settings.removePhoto'),
                onClick: handleRemoveAvatar,
              },
            ],
          }}
          trigger={['click']}
          placement={isMobile ? 'bottom' : 'bottomRight'}
          overlayClassName={classNames(styles.profileDropdown)}
        >
          <div className={classNames(styles.currentPhoto, styles.clickable)}>
            <ProfilePhoto
              key={currentAvatarUrl}
              url={currentAvatarUrl}
              displayName={user.name}
              size={80}
            />
            <div className={classNames(styles.hoverOverlay)}>
              <img src={cameraIcon} alt="camera" width={24} />
            </div>
            {uploading && (
              <div className={classNames(styles.hoverOverlay)}>
                <img src={cameraIcon} alt="camera" width={24} />
              </div>
            )}
          </div>
        </Dropdown>
      </React.Fragment>
    )
  }

  const handleBack = () => {
    if (isMobile && depth === 0) {
      onClose()
      return
    }
    setDepth(0)
    setActiveTab(0)
  }

  const labelStyle = ({ key, IconComponent, text }) => {
    return (
      <div className={classNames(styles.tabItem, styles.calendar)}>
        <div className={classNames(styles.icon)}>
          {IconComponent && <IconComponent color={isMobile ? COLORS.BLACK : changeColor(key)} />}
        </div>
        <div
          className={classNames(styles.label)}
          style={{ color: isMobile ? COLORS.BLACK : changeColor(key) }}
        >
          {text}
        </div>
      </div>
    )
  }

  const items = () => {
    const { role } = props

    return [
      ...(isKR()
        ? [
            {
              key: 6,
              label: labelStyle({
                key: 6,
                IconComponent: AccountIcon,
                text: t('appConfig.settings.account'),
              }),
              children: <AccountRender messageApi={messageApi} />,
            },
          ]
        : []),
      {
        key: 1,
        label: labelStyle({
          key: 1,
          IconComponent: SettingIcon,
          text: t('appConfig.settings.general'),
        }),
        children: (
          <GeneralRender
            messageApi={messageApi}
            userName={userName}
            setIsEditingEmail={setIsEditingEmail}
            isEditingEmail={isEditingEmail}
            isGlobal={isGlobal}
            isSamlUser={isSamlUser}
            renderProfilePhoto={renderProfilePhoto}
          />
        ),
      },
      {
        key: 2,
        label: labelStyle({
          key: 2,
          IconComponent: SecurityIcon,
          text: t('appConfig.settings.security'),
        }),
        children: (
          <SecurityRender
            autoLock={autoLock}
            onAutoLockChange={handleAutoLockChange}
            clipboardClear={clipboardClear}
            onClipboardClearChange={handleClipboardClearChange}
            messageApi={messageApi}
            recoveryChecked={recoveryChecked}
            setRecoveryChecked={setRecoveryChecked}
            locale={locale}
            umkDuration={umkDuration}
            loading={loading}
            setLoading={setLoading}
            recoveryOption={recoveryOption}
            onRecoveryOptionChange={handleRecoveryOptionChange}
            wrkpInitialized={wrkpInitialized}
          />
        ),
      },
      // 패스키 탭 (Figma 685-559) — Security 탭 인라인 섹션에서 분리. isPasskeyEnabled() 게이팅은
      // 기존 Security 내 노출 조건과 동일.
      ...(isPasskeyEnabled()
        ? [
            {
              key: 7,
              label: labelStyle({
                key: 7,
                IconComponent: PasskeyIcon,
                text: t('appConfig.settings.passkey'),
              }),
              children: (
                <div className={classNames(styles.securityContent)}>
                  <PasskeyManagement />
                </div>
              ),
            },
          ]
        : []),
      // 워크스페이스 탭은 글로벌향 전 구성원에게 노출 (2026-07-22 변경) —
      // 이름 편집은 owner/admin 전용, 일반 사용자는 WorkspaceRender 내부에서 읽기 전용으로 표시.
      ...(isGlobal()
        ? [
            {
              key: 3,
              label: labelStyle({
                key: 3,
                IconComponent: CompanyIcon,
                text: t('dashboard.card.workspaces'),
              }),
              children: (
                <WorkspaceRender
                  workspaceName={workspaceNameProps}
                  isEditingTeamName={isEditingTeamName}
                  setIsEditingTeamName={setIsEditingTeamName}
                  ref={ref}
                  messageApi={messageApi}
                  workspaceId={workspaceIdProps}
                  role={role}
                />
              ),
            },
          ]
        : []),
      {
        key: 5,
        label: labelStyle({
          key: 5,
          IconComponent: ZeroDarkWebIcon,
          text: t('appConfig.settings.zeroDarkWeb'),
        }),
        children: (
          <ZeroDarkWebRender settings={zeroDarkWebSettings} onChange={handleZeroDarkWebChange} />
        ),
      },
      {
        key: 4,
        label: labelStyle({
          key: 4,
          IconComponent: AboutIcon,
          text: t('appConfig.settings.about'),
        }),
        children: <AboutRender />,
      },
    ]
  }

  const tabsRef = useRef(null)
  const tabsAttrs = {
    tabPosition: 'left',
    className: classNames(
      styles.tabs,

      isMobile ? styles.mobile : null,
      depth === 0 ? styles.depth0 : styles.depth1,
    ),
    items: items(),
    activeKey: activeTab,
    onChange: (key) => {
      setActiveTab(key)
      setDepth(1)
    },
    centered: true,
  }

  const syncStateFromRedux = useCallback(() => {
    setRecoveryChecked(recoverKeyEnabled === 'email')
    setRecoveryOption(recoveryOptionPersisted || RECOVERY_OPTION.NONE)

    setAutoLock((prev) => {
      if (prev === null) {
        return getInitialAutoLockKey()
      }
      return prev
    })

    setCurrentAvatarUrl(user.avatar)
  }, [recoverKeyEnabled, umkDuration, user.avatar, recoveryOptionPersisted])

  useEffect(() => {
    syncStateFromRedux()
  }, [])

  useEffect(() => {
    if (open) {
      setSettingsOpen(open)
      const latestSettings = getInitialZeroDarkWeb()
      setZeroDarkWebSettings(latestSettings)
      setAutoLock(getInitialAutoLockKey())

      // PR2: ESCROW 전환 대상 ws + Server 옵션 D-GATE용. dev-only(flag) — 운영에선 fetch 안 함.
      if (WRKP_ESCROW_ENABLED) {
        UserService.getMyWrkpPublicKeys()
          .then((res) => setWrkpPublicKeys(res.data?.wrkp_public_keys || []))
          .catch(() => setWrkpPublicKeys([]))
      }

      // register(새 워크스페이스 생성) 직후엔 UMK_LOGIN 경로가 redux user.wrkp_initialized 를
      // 갱신하지 않아 Server(ESCROW) 복구키 저장 옵션이 누락된다(새로고침하면 보임). Settings
      // 오픈 시 /me 권위값으로 재동기화해 새로고침 없이 옵션이 노출되게 한다.
      UserService.me()
        .then((res) => {
          if (res?.code === 200 && res.data?.wrkp_initialized === true) {
            dispatch({ type: 'user/SET_STATE', payload: { wrkp_initialized: true } })
          }
        })
        .catch(() => {})

      if (isMobile) {
        setActiveTab(0)
      } else {
        setActiveTab(isKR() ? 6 : 1)
      }
    } else if (tabsRef.current) {
      if (!isMobile) {
        setActiveTab(isKR() ? 6 : 1)
      } else {
        setActiveTab(0)
        setDepth(0)
      }

      const navList = tabsRef.current.querySelector('.ant-tabs-nav')
      if (navList) {
        setTimeout(() => {
          navList.style.display = ''
        }, 100)
      }
    }
  }, [open, isMobile])

  useEffect(() => {
    const handleOpenZDWSettings = () => {
      const shouldOpen = getTempEnvParam('isOpenZDWSettings') === true

      if (shouldOpen) {
        isOpenZDWSettingsRef.current = true
        setSettingsOpen(true)

        setTimeout(() => {
          if (tabsRef.current) {
            const navList = tabsRef.current.querySelector('.ant-tabs-nav')
            if (navList) {
              navList.style.display = 'none'
            }
          }

          setActiveTab(5)
          const latestSettings = getInitialZeroDarkWeb()
          setZeroDarkWebSettings(latestSettings)
          setDepth(1)
        }, 100)
      }
    }

    window.addEventListener('openZDWSettings', handleOpenZDWSettings)

    return () => {
      window.removeEventListener('openZDWSettings', handleOpenZDWSettings)
    }
  }, [])

  // 모바일 헤더는 breadcrumb 형태(Figma 133-2581 / 751-2996): depth 0 → "설정",
  // depth 1 → "설정 / {탭이름}". 이메일은 헤더에 표시하지 않고 프로필 영역에만 노출한다.
  const settingsLabel = t('topBar.profileMenu.settings')
  const tabTitleMap = {
    6: t('appConfig.settings.account'),
    1: t('appConfig.settings.general'),
    2: t('appConfig.settings.security'),
    7: t('appConfig.settings.passkey'),
    3: t('dashboard.card.workspaces'),
    5: t('appConfig.settings.zeroDarkWeb'),
    4: t('appConfig.settings.about'),
  }
  const mobileHeaderTitle =
    depth === 1 && tabTitleMap[activeTab]
      ? `${settingsLabel} / ${tabTitleMap[activeTab]}`
      : settingsLabel

  const renderHeader = (
    <div>
      {isMobile && !getTempEnvParam('isOpenZDWSettings') && (
        <Button
          type="text"
          className={classNames(styles.headerBackBtn)}
          icon={<img src={backIcon} alt="icons" style={{ width: '24px', height: '24px' }} />}
          onClick={handleBack}
        />
      )}

      {isMobile ? (
        <span className={classNames(styles.headerBreadcrumb)}>{mobileHeaderTitle}</span>
      ) : (
        <>
          <span>{settingsLabel}</span>
          <span className={classNames(styles.userEmail)}>{userEmail}</span>
        </>
      )}
    </div>
  )

  // 모든 설정은 드롭다운/스위치 변경 즉시 반영되므로 저장 버튼(footer)이 필요 없다 — footer 전체 제거.
  // 닫기는 데스크톱 X 아이콘(onCancel), 모바일 뒤로가기 버튼(handleBack)으로 처리.
  const modalAttrs = {
    wrapClassName: classNames('noPadding', styles.settingsModal, 'activityDateFilterModal'),
    title: renderHeader,
    open,
    width: 640,
    height: 480,
    closeIcon: isMobile ? null : <img src={closeIcon} alt="close" />,
    onCancel: handleCancel,
    footer: null,
  }

  const mobileProfile = () => (
    <div className={classNames(styles.profile)}>
      {renderProfilePhoto()}
      <span className={classNames(styles.workspaceName)}>{user?.workspace?.workspace_name}</span>
      <div className={classNames(styles.nameEmail)}>
        <span className={classNames(styles.name)}>{user.name}</span>
        <span className={classNames(styles.email)}>{user.email}</span>
      </div>
    </div>
  )

  return (
    <Modal {...modalAttrs}>
      <div className={classNames(styles.settings)} ref={tabsRef}>
        {isMobile && depth === 0 && mobileProfile()}
        <Tabs {...tabsAttrs} />
        {contextHolder}
      </div>
    </Modal>
  )
}

export default connect(mapStateToProps)(SettingsPopup)
